import { useState } from "react";
import { Mail, Phone, MapPin, Send, Linkedin, Twitter, Facebook, Instagram } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const INDUSTRIES = ["Banking & Financial Services", "Telecommunications", "Retail", "Betting & Gaming", "FMCG", "Automotive", "Healthcare", "Pharmaceuticals", "Insurance", "Real Estate", "Tourism & Hospitality", "Restaurants", "Fast Food", "Education", "Sports", "Entertainment", "Events", "Shopping Malls", "Government", "Media", "Advertising Agency", "Logistics", "Travel", "Technology", "SMME", "Other"];
const CAMPAIGN_TYPES = ["Brand Awareness", "Product Launch", "Event Activation", "Store Footfall", "App Installs", "Lead Generation", "Other"];

export default function Contact() {
  const [form, setForm] = useState({ first_name: "", last_name: "", company: "", email: "", phone: "", industry: "", campaign_type: "", num_locations: "", message: "" });
  const [loading, setLoading] = useState(false);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target ? e.target.value : e });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Request received. Our team will contact you shortly.");
      setForm({ first_name: "", last_name: "", company: "", email: "", phone: "", industry: "", campaign_type: "", num_locations: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
      <div className="grid lg:grid-cols-[1fr_1.4fr] gap-12">
        <div>
          <p className="section-label">Contact Us</p>
          <h1 className="mt-4 font-display text-4xl sm:text-5xl font-bold text-white tracking-tight">
            LET'S ACTIVATE YOUR <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-300">LOCATIONS</span>
          </h1>
          <p className="mt-5 text-slate-400 leading-relaxed">
            Tell us about your campaign goals and our location intelligence team will design a geofencing strategy for you.
          </p>
          <div className="mt-10 space-y-5">
            {[
              { icon: Mail, label: "Email", value: "hello@geotraq.ai" },
              { icon: Phone, label: "Phone", value: "+27 11 025 3400" },
              { icon: MapPin, label: "Johannesburg Office", value: "1 Fredman Drive, Sandton, Johannesburg, 2196" },
            ].map((c) => (
              <div key={c.label} className="flex items-start gap-4" data-testid={`contact-info-${c.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <span className="w-10 h-10 rounded-md bg-blue-600/15 border border-blue-500/25 flex items-center justify-center shrink-0">
                  <c.icon className="w-4 h-4 text-cyan-400" />
                </span>
                <div>
                  <p className="text-xs tracking-widest text-slate-500 uppercase">{c.label}</p>
                  <p className="text-sm text-white mt-1">{c.value}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="flex gap-3 mt-10">
            {[Linkedin, Twitter, Facebook, Instagram].map((Icon, i) => (
              <a key={i} href="#" aria-label="Social" className="w-9 h-9 rounded-md border border-white/10 flex items-center justify-center text-slate-400 hover:text-cyan-400 hover:border-cyan-500/40 transition-colors">
                <Icon className="w-4 h-4" />
              </a>
            ))}
          </div>
        </div>

        <form onSubmit={submit} className="card-surface p-7 lg:p-9 space-y-5" data-testid="contact-form">
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-300">First Name *</Label><Input required value={form.first_name} onChange={set("first_name")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-first-name" /></div>
            <div><Label className="text-slate-300">Last Name *</Label><Input required value={form.last_name} onChange={set("last_name")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-last-name" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-300">Company</Label><Input value={form.company} onChange={set("company")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-company" /></div>
            <div><Label className="text-slate-300">Email *</Label><Input required type="email" value={form.email} onChange={set("email")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-email" /></div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div><Label className="text-slate-300">Phone</Label><Input value={form.phone} onChange={set("phone")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-phone" /></div>
            <div>
              <Label className="text-slate-300">Industry</Label>
              <Select value={form.industry} onValueChange={set("industry")}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-industry"><SelectValue placeholder="Select industry" /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10 max-h-64">
                  {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid sm:grid-cols-2 gap-5">
            <div>
              <Label className="text-slate-300">Campaign Type</Label>
              <Select value={form.campaign_type} onValueChange={set("campaign_type")}>
                <SelectTrigger className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-campaign-type"><SelectValue placeholder="Select type" /></SelectTrigger>
                <SelectContent className="bg-popover border-white/10">
                  {CAMPAIGN_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div><Label className="text-slate-300">Number of Locations</Label><Input value={form.num_locations} onChange={set("num_locations")} placeholder="e.g. 3" className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-num-locations" /></div>
          </div>
          <div><Label className="text-slate-300">Message</Label><Textarea rows={4} value={form.message} onChange={set("message")} className="mt-1.5 bg-secondary/50 border-white/10" data-testid="contact-message" /></div>
          <Button type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-500 font-semibold h-11" data-testid="contact-submit">
            {loading ? "SENDING..." : "REQUEST A CAMPAIGN CONSULTATION"} <Send className="w-4 h-4 ml-2" />
          </Button>
        </form>
      </div>
    </div>
  );
}
